/**
 * Página aberta pelo QR do certificado de treinamento, sem login.
 * Tarefa: saber se o certificado em mãos é autêntico e ainda vale.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  CheckCircle as ValidoIcon,
  ErrorOutline as AvisoIcon,
  Cancel as CanceladoIcon,
  HelpOutline as NaoEncontradoIcon,
  Info as FalhaIcon,
} from '@mui/icons-material';
import {
  certificadoPublicoService,
  CertificadoNaoEncontradoError,
  type CertificadoPublico,
} from '../../services/certificadoPublicoService';
import { dataBrasileira } from '../../utils/vaccinationDates';

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'resultado'; certificado: CertificadoPublico }
  | { tipo: 'nao-encontrado' }
  | { tipo: 'falha' };

type Tom = 'success' | 'warning' | 'error' | 'neutral';

const FUNDO: Record<Tom, string> = {
  success: '#E6F4EA',
  warning: '#FFF4D6',
  error: '#FDE7E7',
  neutral: '#EEF1F4',
};
const COR_ICONE: Record<Tom, string> = {
  success: '#1B5E20',
  warning: '#8A5A00',
  error: '#B71C1C',
  neutral: '#37474F',
};

const Veredito: React.FC<{ tom: Tom; icone: React.ReactNode; titulo: string; texto: string }> = ({
  tom,
  icone,
  titulo,
  texto,
}) => (
  <Box
    role="status"
    sx={{ display: 'flex', gap: 2, p: 2, borderRadius: 1, bgcolor: FUNDO[tom], color: '#1F2933' }}
  >
    <Box sx={{ color: COR_ICONE[tom], display: 'flex', '& svg': { fontSize: 40 } }}>{icone}</Box>
    <Box>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
        {titulo}
      </Typography>
      <Typography sx={{ fontSize: 16 }}>{texto}</Typography>
    </Box>
  </Box>
);

const Dado: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <>
    <Typography component="dt" sx={{ fontSize: 14, color: 'text.secondary', mt: 1.5 }}>
      {rotulo}
    </Typography>
    <Typography component="dd" sx={{ fontSize: 18, fontWeight: 500, m: 0, wordBreak: 'break-word' }}>
      {valor}
    </Typography>
  </>
);

/** "29/10/2026 às 14:32" no fuso de Brasília, a partir do instante ISO. */
function conferidoEmTexto(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  const dia = data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  const hora = data.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dia} às ${hora}`;
}

export const CertificadoValidacaoPage: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>();
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });

  const conferir = useCallback(async () => {
    try {
      if (!codigo) throw new CertificadoNaoEncontradoError();
      const certificado = await certificadoPublicoService.conferir(codigo);
      setEstado({ tipo: 'resultado', certificado });
    } catch (err: unknown) {
      setEstado({ tipo: err instanceof CertificadoNaoEncontradoError ? 'nao-encontrado' : 'falha' });
    }
  }, [codigo]);

  const tentarDeNovo = () => {
    setEstado({ tipo: 'carregando' });
    conferir();
  };

  useEffect(() => {
    conferir();
  }, [conferir]);

  let conteudo: React.ReactNode;

  if (estado.tipo === 'carregando') {
    conteudo = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Conferindo o certificado" />
      </Box>
    );
  } else if (estado.tipo === 'nao-encontrado') {
    conteudo = (
      <Veredito
        tom="neutral"
        icone={<NaoEncontradoIcon />}
        titulo="Certificado não encontrado"
        texto="Não existe certificado com este código. Confira o código impresso no documento ou peça um novo à empresa que o emitiu."
      />
    );
  } else if (estado.tipo === 'falha') {
    conteudo = (
      <>
        <Veredito
          tom="neutral"
          icone={<FalhaIcon />}
          titulo="Não foi possível conferir agora"
          texto="O certificado não foi recusado: a conferência falhou. Tente de novo em alguns instantes."
        />
        <Button variant="contained" size="large" onClick={tentarDeNovo} sx={{ alignSelf: 'flex-start', minHeight: 48 }}>
          Tentar de novo
        </Button>
      </>
    );
  } else {
    const c = estado.certificado;
    const conferido = conferidoEmTexto(c.conferidoEm);
    if (c.situacao === 'cancelado') {
      conteudo = (
        <>
          <Veredito
            tom="error"
            icone={<CanceladoIcon />}
            titulo="Certificado cancelado"
            texto="Este treinamento foi cancelado. Não use este certificado."
          />
          <Box component="dl" sx={{ m: 0 }}>
            <Dado rotulo="Treinamento" valor={c.treinamento} />
          </Box>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            {conferido && `Conferido em ${conferido}. `}Nome e datas não são exibidos.
          </Typography>
        </>
      );
    } else {
      const vencido = c.situacao === 'vencido';
      conteudo = (
        <>
          {vencido ? (
            <Veredito
              tom="warning"
              icone={<AvisoIcon />}
              titulo="Certificado vencido"
              texto={`O certificado é autêntico, mas a validade terminou em ${dataBrasileira(c.validoAte)}.`}
            />
          ) : (
            <Veredito
              tom="success"
              icone={<ValidoIcon />}
              titulo="Certificado válido"
              texto="Este certificado foi emitido pelo MEV-Med e o treinamento está vigente."
            />
          )}
          <Box component="dl" sx={{ m: 0 }}>
            <Dado rotulo="Nome" valor={c.nome} />
            <Dado rotulo="Treinamento" valor={c.treinamento} />
            {c.cargaHoraria !== null && <Dado rotulo="Carga horária" valor={`${c.cargaHoraria} horas`} />}
            <Dado rotulo="Realizado em" valor={dataBrasileira(c.realizadoEm)} />
            <Dado rotulo="Válido até" valor={dataBrasileira(c.validoAte)} />
            <Dado rotulo="Empresa" valor={c.empresa} />
          </Box>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            {conferido && `Conferido em ${conferido}. `}A situação é consultada a cada acesso.
          </Typography>
        </>
      );
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA', display: 'flex', justifyContent: 'center', p: { xs: 1, sm: 3 } }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 560,
          alignSelf: 'flex-start',
          bgcolor: '#fff',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 1,
        }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#0F3D4C', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>MEV-Med</Typography>
          <Typography component="span" sx={{ fontSize: 16 }}>Validação de certificado</Typography>
        </Box>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>{conteudo}</Box>
      </Box>
    </Box>
  );
};

export default CertificadoValidacaoPage;
